import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { ISubStep } from "../declarations/IFunctionsResponse";

interface LogEntry {
    phase: string;
    timestamp: Date;
    status: "active" | "completed";
    customMessage?: string;
}

interface IChildOrchestrationMap {
    [phaseName: string]: ISubStep[];
}

interface TerminalLogProps {
    childOrchestrations?: IChildOrchestrationMap;
    currentPhase?: string;
    customCompletionMessage?: string;
    isCompleted?: boolean;
}

export default function TerminalLog({
    childOrchestrations,
    currentPhase,
    customCompletionMessage,
    isCompleted,
}: TerminalLogProps) {
    const [logEntries, setLogEntries] = useState<LogEntry[]>([]);

    useEffect(() => {
        if (!currentPhase) return;

        setLogEntries((prev) => {
            // Mark previous active entry as completed
            const updated = prev.map((entry) =>
                entry.status === "active"
                    ? { ...entry, status: "completed" as const }
                    : entry,
            );

            // Check if this phase is already logged
            const existingEntry = updated.find(
                (entry) => entry.phase === currentPhase,
            );

            if (!existingEntry) {
                // Add new active phase
                updated.push({
                    phase: currentPhase,
                    timestamp: new Date(),
                    status: "active",
                });
            }

            return updated;
        });
    }, [currentPhase]);

    // Handle completion
    useEffect(() => {
        if (isCompleted) {
            setLogEntries((prev) =>
                prev.map((entry) =>
                    entry.status === "active"
                        ? {
                              ...entry,
                              status: "completed" as const,
                              customMessage: customCompletionMessage,
                          }
                        : entry,
                ),
            );
        }
    }, [isCompleted, customCompletionMessage]);

    const formatTimestamp = (date: Date) => {
        return date.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const formatIsoTimestamp = (iso: string) => {
        const date = new Date(iso);
        return date.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const getStatusText = (entry: LogEntry) => {
        if (entry.status === "completed") {
            return entry.customMessage || "...Done";
        }
        return "...";
    };

    const getStatusColor = (entry: LogEntry) => {
        switch (entry.status) {
            case "active":
                return "primary.main";
            case "completed":
                return "success.main";
            default:
                return "text.primary";
        }
    };

    const hasActiveSubStep = (subSteps: ISubStep[]) =>
        subSteps.some((s) => s.status === "Active");

    const hasAnyActive =
        logEntries.some((entry) => entry.status === "active") ||
        Object.values(childOrchestrations ?? {}).some(hasActiveSubStep);

    const renderSubSteps = (phaseName: string) => {
        const subSteps = childOrchestrations?.[phaseName];
        if (!subSteps || subSteps.length === 0) return null;

        return subSteps.map((step, idx) => {
            const isLast = idx === subSteps.length - 1;
            const branch = isLast ? "\u2514" : "\u251C";
            const isActive = step.status === "Active";

            return (
                <Box key={idx} sx={{ mb: 0.25 }}>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            color: "#569cd6",
                        }}
                    >
                        [{formatIsoTimestamp(step.timestamp)}]
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            color: "#6a737d",
                            ml: 1,
                        }}
                    >
                        {branch}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            color: "#9da5b4",
                            ml: 0.5,
                        }}
                    >
                        {step.name}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            color: isActive ? "primary.main" : "success.main",
                            ml: 1,
                        }}
                    >
                        {isActive ? "..." : "...Done"}
                    </Typography>
                    {isActive && (
                        <Box
                            component="span"
                            sx={{
                                display: "inline-block",
                                width: "8px",
                                height: "14px",
                                backgroundColor: "#d4d4d4",
                                animation: "blink 1s infinite",
                                ml: 0.5,
                                "@keyframes blink": {
                                    "0%, 50%": { opacity: 1 },
                                    "51%, 100%": { opacity: 0 },
                                },
                            }}
                        />
                    )}
                </Box>
            );
        });
    };

    return (
        <Box
            sx={{
                backgroundColor: "#1e1e1e",
                color: "#d4d4d4",
                fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                fontSize: "13px",
                padding: 2,
                borderRadius: 1,
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #333",
            }}
        >
            {logEntries.length === 0 ? (
                <Typography
                    sx={{
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        color: "text.disabled",
                    }}
                >
                    Waiting for process to start...
                </Typography>
            ) : (
                logEntries.map((entry, index) => (
                    <React.Fragment key={index}>
                        <Box sx={{ mb: 0.5 }}>
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    color: "#569cd6",
                                }}
                            >
                                [{formatTimestamp(entry.timestamp)}]
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    color: "#d4d4d4",
                                    ml: 1,
                                }}
                            >
                                {entry.phase}
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    color: getStatusColor(entry),
                                    ml: 1,
                                }}
                            >
                                {getStatusText(entry)}
                            </Typography>
                        </Box>
                        {renderSubSteps(entry.phase)}
                    </React.Fragment>
                ))
            )}
            {hasAnyActive && !Object.values(childOrchestrations ?? {}).some(hasActiveSubStep) && (
                <Box
                    component="span"
                    sx={{
                        display: "inline-block",
                        width: "8px",
                        height: "14px",
                        backgroundColor: "#d4d4d4",
                        animation: "blink 1s infinite",
                        ml: 0.5,
                        "@keyframes blink": {
                            "0%, 50%": { opacity: 1 },
                            "51%, 100%": { opacity: 0 },
                        },
                    }}
                />
            )}
        </Box>
    );
}
